import { attachEventsHook } from '@olerex/web-events-hook';
import 'babel-polyfill';
import 'react-app-polyfill/ie11';
import React from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import App from './App';
import * as serviceWorker from './serviceWorker';
import { createBrowserHistory } from 'history';
import { createStore, combineReducers, applyMiddleware } from 'redux';
import { connectRouter, routerMiddleware, ConnectedRouter } from 'connected-react-router';
import thunkMiddleware from 'redux-thunk';
import { Provider } from 'react-redux';
import login, { initialLoginState } from './state/Login';
import { setAccessToken } from './auth/accessToken';
import size, { initialWindowSizeState } from './state/Size';
import translation, { initialTranslationState } from './state/Translation';
import error, { initialErrorState } from './state/Error';
import { Switch, Route } from 'react-router-dom';

const history = createBrowserHistory();

// The native app reuses a single WebView (shared localStorage), so a persisted
// "login" survives switching users / logging out in the app. Without dropping it,
// every webview screen keeps showing the previous user.
//
// App sessions now carry the auth-service access JWT (?token=, preferred); browser
// and legacy sessions carry an opaque nonce (?nonce=). For a JWT we drop the cached
// session only when the `usrid` claim differs (a real user switch) — the JWT rotates
// every ~15 min, so comparing the raw string would needlessly re-authenticate on
// every open. For a nonce we keep the legacy behaviour: drop when the nonce that
// created the cached session (authNonce) no longer matches.
const readPersistedLogin = () => {
    try {
        return JSON.parse(localStorage.getItem("login"));
    } catch (e) {
        return null;
    }
};

// The login credential from the URL — prefer the JWT (app), fall back to the nonce.
const getUrlCredential = () => {
    try {
        const params = new URLSearchParams(window.location.search);
        const token = params.get("token");
        if (token && token.split(".").length === 3) {
            return token;
        }
        return params.get("nonce") || token || null;
    } catch (e) {
        return null;
    }
};

// usrid from a JWT credential. UNVERIFIED — this only decides whether the cached
// session belongs to the same user; the real auth happens server-side in
// validateNonce. Returns null for non-JWT (nonce) credentials.
const getCredentialUserId = (credential) => {
    if (!credential || credential.split(".").length !== 3) {
        return null;
    }
    try {
        let b64 = credential.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
        b64 += "===".slice((b64.length + 3) % 4);
        const usrid = parseInt(JSON.parse(atob(b64)).usrid, 10);
        return Number.isNaN(usrid) ? null : usrid;
    } catch (e) {
        return null;
    }
};

const persistedLogin = readPersistedLogin();
const urlCredential = getUrlCredential();
const urlUserId = getCredentialUserId(urlCredential);
const persistedUserId = parseInt(persistedLogin?.privateClientData?.userId, 10);

const staleSession = (urlUserId && persistedUserId)
    ? urlUserId !== persistedUserId // JWT: only a real user switch is stale
    : Boolean(persistedLogin && urlCredential && persistedLogin.authNonce !== urlCredential); // nonce/legacy

if (staleSession) {
    localStorage.removeItem("login");
}

const store = createStore(
    combineReducers({ router: connectRouter(history), ...{ login, size, translation, error /* other state reducers */ } }),
    { login: staleSession ? { ...initialLoginState } : { ...initialLoginState, ...persistedLogin, sessionExpired: false }, size: initialWindowSizeState, translation: initialTranslationState, error: initialErrorState }, //load initial state
    applyMiddleware(thunkMiddleware, routerMiddleware(history))
);

// Keep the in-memory access-token owner in sync with redux `login.token`, so
// authenticated requests source the token from one place. Seed it from the
// hydrated state before the first render, then track every change.
setAccessToken(store.getState().login?.token);
store.subscribe(() => setAccessToken(store.getState().login?.token));

if (process.env.NODE_ENV === 'production') {
    // Start listening on console events and uncaught errors
    const hook = attachEventsHook({
        url: 'https://api.olerex.ee/v2/olerex.web.event?apiKey=jIEDvOaIvLzU7Utxi9-lql5dmCJPfnEG5M5bg_QAe0KOSoWrP0gidQ',
    });

    store.subscribe(() => {
        const state = store.getState();
        const accNr = parseInt(state.login?.currentRole?.accnr, 10);
        const userId = parseInt(state.login?.currentRole?.userId, 10);

        hook.setLabel('ss.role.accNr', accNr || undefined);
        hook.setLabel('ss.role.userId', userId || undefined);
    });
}

//save login to local storage
store.subscribe(() => {
    const state = store.getState();
    if (state.login) {
        localStorage.setItem("login", JSON.stringify(state.login));
    } else {
        localStorage.removeItem("login");
    }
});

//update the document title
store.subscribe(() => {
    const state = store.getState();
    const pathname = state.router && state.router.location && state.router.location.pathname;
    const translation = state.translation && state.translation.title;
    const title = (translation && pathname && translation[pathname]) || "Olerex";
    document.title = title;
});

//App itself in a route or the back button will not with inner routes
ReactDOM.render(
    <Provider store={store}>
        <ConnectedRouter history={history}>
            <Switch>
                <Route path="/" component={App} />
            </Switch>
        </ConnectedRouter>
    </Provider>,
    document.getElementById('root'));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
